/*----------------------------------
- TERMS PAGE CONTENT CATALOG (SSOT)
----------------------------------*/

import { ANALYSES_RESET_PHRASE } from '@uniquedomains/components/pricing/catalogs/analysesReset';
import { ANALYSIS_ACCESS_DAYS } from '@uniquedomains/core/domains/analysis';
import { AVAILABILITY_RUN_SIZE } from '@uniquedomains/components/contracts/lookup/limits';
import { PUBLIC_PLAN_FACTS } from '@uniquedomains/components/pricing/catalogs/pricingPlans';
import { FULL_ACCESS_RADARS } from '@uniquedomains/components/pricing/catalogs/radarOfferCopy';
import { PAYMENT_FAILURE_GRACE_LABEL } from '@uniquedomains/components/pricing/catalogs/paymentGrace';
import { REFUND_GUARANTEE_DAYS } from '@uniquedomains/components/pricing/catalogs/refundPromise';
import type { LegalPageContent } from '@/client/catalogs/landing/legal/types';

/*----------------------------------
- FIGURES
----------------------------------*/

const MEMBER_FACTS = PUBLIC_PLAN_FACTS.member;
const EXPORT_ROWS = MEMBER_FACTS.exportRows.toLocaleString('en-US');

/**
 * Every statement here is derived from shipped behavior, not from intent:
 * the plan, its price and its limits from `PUBLIC_PLAN_FACTS` (held to
 * `apps/product/server/config/plans.ts` by a contract test), the refund days from
 * `refundPromise.ts`, the paid-only access model from ADR-0083 (no free plan, no
 * trial, no credits shown to members), and the payment-failure rule from
 * `PAYMENT_FAILURE_GRACE_HOURS` in `packages/components/pricing/catalogs/paymentGrace.ts`, the
 * number Product's access predicate reads (D23 of the paid-funnel plan). Figures are interpolated from those
 * constants: "$19" was typed in section 4 and outlived the price it described.
 *
 * This page needs a legal review before it is indexed. The entity gate in
 * `apps/website/common/catalogs/legal/entity.ts` keeps it out of the index
 * until the trader details are real.
 */

export const TERMS_PAGE_CONTENT: LegalPageContent = {
    meta: {
        pageTitle: 'Terms of Service | Unique Domains',
        metaDescription:
            'The terms that apply when you use Unique Domains: what the product does, how the plan and its limits are billed, how refunds work, and how to cancel.',
    },
    hero: {
        heading: 'Terms of Service',
        intro: 'These terms apply when you create an account or buy a plan. They describe what Unique Domains does, what it does not do, and exactly how billing works.',
    },
    sections: [
        {
            id: 'who-we-are',
            heading: '1. Who we are',
            blocks: [
                {
                    kind: 'paragraph',
                    text: 'Unique Domains is operated by the company identified at the top of this page. You can reach us at the contact address listed there for any question about these terms.',
                },
            ],
        },
        {
            id: 'what-the-service-does',
            heading: '2. What the service does',
            blocks: [
                {
                    kind: 'paragraph',
                    text: 'Unique Domains helps you find and evaluate one-word domain names. It gives you search, saved searches, monitoring, valuation estimates, and decision reports.',
                },
                {
                    kind: 'paragraph',
                    text: 'It is important to be clear about what the service is not, because these limits shape everything else in this document:',
                },
                {
                    kind: 'list',
                    items: [
                        'We are not a registrar. We do not sell, register, or renew domain names. We link you to registrars and marketplaces, and you buy from them under their terms.',
                        'We are not a broker or an escrow service. We take no commission on any purchase or sale you make.',
                        'We do not provide legal advice. The trademark check is a signal to help you spot obvious conflicts. Confirm with a qualified lawyer before you commit to a brand.',
                        'We do not provide investment advice. Valuation estimates are automated third-party estimates, not appraisals or guarantees of resale value.',
                    ],
                },
            ],
        },
        {
            id: 'accounts',
            heading: '3. Your account',
            blocks: [
                {
                    kind: 'paragraph',
                    text: 'You need an account to use the product. Sign-in is passwordless: you receive a six-digit code or a sign-in link by email, or you sign in with Google. You are responsible for keeping access to the mailbox tied to your account.',
                },
                {
                    kind: 'paragraph',
                    text: 'An account belongs to one person. Plans that include additional seats let you invite teammates to your whole workspace, and the pricing page states how many each plan includes. Client review links are different: they let someone outside the account vote on one list without an account, they use no seat, and you can revoke any of them at any time.',
                },
            ],
        },
        {
            id: 'plans-and-billing',
            heading: '4. Plans and billing',
            blocks: [
                {
                    kind: 'paragraph',
                    text: 'Payments are processed by Stripe. We never see or store your full card details. Prices are shown in US dollars and exclude any tax that may apply to you.',
                },
                {
                    kind: 'list',
                    items: [
                        `Full Access is a subscription of $${MEMBER_FACTS.price} per month. It renews automatically until you cancel, and it carries no trial period: the first charge lands at checkout.`,
                        'There is no free plan. An account without Full Access is free to keep: it holds your saved brief and shows how many names match it, without the live data.',
                        `One seat is included. Each extra seat is $${MEMBER_FACTS.extraSeatPrice} per month, billed on the same subscription. Adding one is charged for the rest of the current period straight away; removing one takes effect at the next renewal.`,
                        'Subscriptions to plans that are no longer sold, including Launch, Pro, Agency and earlier Full Access subscriptions, now carry Full Access and renew at the price they were purchased at until they are cancelled.',
                    ],
                },
            ],
        },
        {
            /*
             * This slot held "The Pro trial": 3 free days converting to $79 or
             * $660. No plan has a trial and neither price exists in Stripe, so
             * it stated billing terms the code cannot honour and contradicted
             * section 4 of the same contract. The refund is what replaced the
             * trial commercially, so it takes the same position rather than
             * shifting every heading number below it.
             */
            id: 'refunds',
            heading: '5. Refunds',
            blocks: [
                {
                    kind: 'paragraph',
                    text: `No plan has a trial. Your card is charged at checkout, and a refund is what replaces the trial: tell us within ${REFUND_GUARANTEE_DAYS} days of paying and we refund you in full, with no conditions. It holds even if you already used your monthly analyses. That guarantee can be used once per customer.`,
                },
                {
                    kind: 'paragraph',
                    text: 'If you are a consumer in the European Union, your 14-day right of withdrawal runs on its own terms alongside this, and whichever gives you more is the one that applies. Our refund policy page sets out both in full, together with the rules for renewals and duplicate charges.',
                },
            ],
        },
        {
            /* "6. Credits" until ADR-0083: Full Access now sells its limits as things, and no
               member surface prices an action in credits. */
            id: 'limits',
            heading: '6. Monthly limits',
            blocks: [
                {
                    kind: 'paragraph',
                    text: `Full Access includes ${MEMBER_FACTS.analysesPerMonth} full domain analyses a month, up to ${FULL_ACCESS_RADARS} Radars, appraisals of up to ${AVAILABILITY_RUN_SIZE.fullAccess} names a run, and CSV exports of up to ${EXPORT_ROWS} rows per export.`,
                },
                {
                    kind: 'list',
                    items: [
                        `The analysis count ${ANALYSES_RESET_PHRASE}. Analyses you did not run do not carry over.`,
                        `An analysis stays open for ${ANALYSIS_ACCESS_DAYS} days. An appraisal already stored costs nothing to read again.`,
                        'Subscriptions to plans no longer sold carry the Full Access limits above, at the price they were purchased at.',
                    ],
                },
                {
                    kind: 'paragraph',
                    text: 'Limits are not refundable units and have no cash value.',
                },
            ],
        },
        {
            id: 'cancellation',
            heading: '7. Cancelling',
            blocks: [
                {
                    kind: 'paragraph',
                    text: 'You can cancel a subscription at any time from the billing portal in your account settings. Cancelling stops the next renewal. Your plan stays active until the end of the period you have already paid for, and you are not charged again.',
                },
                {
                    kind: 'paragraph',
                    text: 'When a paid plan ends, your account keeps your brief, saved searches, lists and Radars, without the live data, and they open again if you subscribe again.',
                },
            ],
        },
        {
            id: 'failed-payments',
            heading: '8. Failed payments',
            blocks: [
                {
                    kind: 'paragraph',
                    text: `If a renewal payment is refused, Full Access stays open for ${PAYMENT_FAILURE_GRACE_LABEL} so you can update your payment method. After that the plan ends until the payment goes through. You keep your data either way.`,
                },
            ],
        },
        {
            id: 'acceptable-use',
            heading: '9. Acceptable use',
            blocks: [
                {
                    kind: 'paragraph',
                    text: 'Use the product for your own domain research. Do not resell, redistribute, or republish the database, and do not scrape it or work around the limits of your plan.',
                },
                {
                    kind: 'paragraph',
                    text: 'Programmatic access through the REST API is not open yet. When it opens, it will run within the published request limits. We may suspend access that threatens the stability of the service.',
                },
            ],
        },
        {
            id: 'data-accuracy',
            heading: '10. Accuracy of the data',
            blocks: [
                {
                    kind: 'paragraph',
                    text: 'We collect domain data from registries, registrars and third-party sources, and we refresh it on a rolling schedule. Availability, pricing and status can change between our last check and the moment you act. Always confirm availability and price with the registrar before you buy.',
                },
                {
                    kind: 'paragraph',
                    text: 'Scores, ratings and valuation estimates are automated. They are there to help you compare candidates, not to tell you what a name is worth.',
                },
            ],
        },
        {
            id: 'availability',
            heading: '11. Availability and changes',
            blocks: [
                {
                    kind: 'paragraph',
                    text: 'We aim to keep the service available, but we do not guarantee uninterrupted access. We may add, change or remove features. If we make a change that materially reduces what your paid plan includes, we will tell you by email before it takes effect.',
                },
            ],
        },
        {
            id: 'liability',
            heading: '12. Liability',
            blocks: [
                {
                    kind: 'paragraph',
                    text: 'The service is provided as it is. To the extent the law allows, we are not liable for indirect or consequential loss, including lost profit or lost opportunity on a domain purchase. Nothing here limits liability that cannot be limited by law, including liability for death, personal injury, or fraud.',
                },
                {
                    kind: 'paragraph',
                    text: 'Where our liability is limited, it is limited to the amount you paid us in the twelve months before the claim.',
                },
            ],
        },
        {
            id: 'law',
            heading: '13. Governing law',
            blocks: [
                {
                    kind: 'paragraph',
                    text: 'These terms are governed by the law of the country in which our company is registered, as stated at the top of this page. If you are a consumer, you keep the protection of the mandatory law of the country where you live.',
                },
            ],
        },
        {
            id: 'contact',
            heading: '14. Contact',
            blocks: [
                {
                    kind: 'paragraph',
                    text: 'Write to the contact address at the top of this page for anything relating to these terms, your account, or a payment.',
                },
            ],
        },
    ],
};
